import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { studentsApi, partnersApi, coursesApi, sessionsApi, materialsApi, gradesApi, certificatesApi, teacherApi, studentApi, usersApi, dossiersApi, documentsApi, appointmentsApi, paymentsApi, messagingApi, notificationsApi, statsApi, avatarApi, cronApi, resourcesApi } from './api';

// Students hooks
export const useStudents = (filters = {}) => {
  return useQuery({
    queryKey: ['students', filters],
    queryFn: () => studentsApi.getAll(filters),
  });
};

export const useStudent = (id) => {
  return useQuery({
    queryKey: ['student', id],
    queryFn: () => studentsApi.getById(id),
    enabled: !!id,
  });
};

export const useStudentAuditLogs = (id, options = {}) => {
  return useQuery({
    queryKey: ['student-audit-logs', id],
    queryFn: () => studentsApi.getAuditLogs(id),
    enabled: !!id && (options.enabled !== false),
  });
};

export const useCreateStudent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: studentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
    },
  });
};

export const useUpdateStudent = (id) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => studentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student', id] });
    },
  });
};

export const useUpdateStudentStatus = (id) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (status) => studentsApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['student', id] });
      queryClient.invalidateQueries({ queryKey: ['student-audit-logs', id] });
    },
  });
};

export const useDeleteStudent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: studentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
    },
  });
};

// Partners hooks
export const usePartners = (params = {}) => {
  return useQuery({
    queryKey: ['partners', params],
    queryFn: () => partnersApi.getAll(params),
  });
};

export const usePartner = (partnerId) => {
  return useQuery({
    queryKey: ['partner', partnerId],
    queryFn: () => partnersApi.getById(partnerId),
    enabled: !!partnerId,
  });
};

export const useMyPartnerProfile = () => {
  return useQuery({
    queryKey: ['my-partner-profile'],
    queryFn: partnersApi.getMyProfile,
  });
};

export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: partnersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
};

export const useUpdatePartner = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => partnersApi.update(partnerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
    },
  });
};

export const useDeletePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: partnersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
};

export const usePartnerCandidates = (partnerId) => {
  return useQuery({
    queryKey: ['partner-candidates', partnerId],
    queryFn: () => partnersApi.getCandidates(partnerId),
    enabled: !!partnerId,
  });
};

export const useAssignCandidate = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studentId) => partnersApi.assignCandidate(partnerId, studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-candidates', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
    },
  });
};

export const useUpdateCandidateStatus = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, data }) => partnersApi.updateCandidateStatus(partnerId, studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-candidates', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['my-partner-profile'] });
    },
  });
};

export const useRemoveCandidate = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studentId) => partnersApi.removeCandidate(partnerId, studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-candidates', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
    },
  });
};

export const usePartnerNeeds = (partnerId) => {
  return useQuery({
    queryKey: ['partner-needs', partnerId],
    queryFn: () => partnersApi.getNeeds(partnerId),
    enabled: !!partnerId,
  });
};

export const useCreatePartnerNeed = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => partnersApi.createNeed(partnerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-needs', partnerId] });
    },
  });
};

export const useDeletePartnerNeed = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (needId) => partnersApi.deleteNeed(partnerId, needId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-needs', partnerId] });
    },
  });
};

export const useIntegrationStatus = (partnerId, studentId) => {
  return useQuery({
    queryKey: ['integration', partnerId, studentId],
    queryFn: () => partnersApi.getIntegration(partnerId, studentId),
    enabled: !!partnerId && !!studentId,
  });
};

export const useUpdateIntegration = (partnerId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, data }) => partnersApi.updateIntegration(partnerId, studentId, data),
    onSuccess: (_, { studentId }) => {
      queryClient.invalidateQueries({ queryKey: ['integration', partnerId, studentId] });
      queryClient.invalidateQueries({ queryKey: ['partner-candidates', partnerId] });
    },
  });
};

// ==================== COURSES HOOKS ====================

export const useCourses = (filters = {}) => {
  return useQuery({
    queryKey: ['courses', filters],
    queryFn: () => coursesApi.getAll(filters),
  });
};

export const useCourse = (id) => {
  return useQuery({
    queryKey: ['course', id],
    queryFn: () => coursesApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: coursesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

export const useUpdateCourse = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => coursesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      queryClient.invalidateQueries({ queryKey: ['course', id] });
    },
  });
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: coursesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

export const useEnrolledStudents = (courseId) => {
  return useQuery({
    queryKey: ['enrolled-students', courseId],
    queryFn: () => coursesApi.getEnrolledStudents(courseId),
    enabled: !!courseId,
  });
};

export const useEnrollStudent = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studentId) => coursesApi.enrollStudent(courseId, studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrolled-students', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

export const useUnenrollStudent = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studentId) => coursesApi.unenrollStudent(courseId, studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrolled-students', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

// ==================== SESSIONS HOOKS ====================

export const useSessions = (courseId) => {
  return useQuery({
    queryKey: ['sessions', courseId],
    queryFn: () => sessionsApi.getByCourse(courseId),
    enabled: !!courseId,
  });
};

export const useSession = (id) => {
  return useQuery({
    queryKey: ['session', id],
    queryFn: () => sessionsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateSession = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sessionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
  });
};

export const useDeleteSession = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sessionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions', courseId] });
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
  });
};

export const useAttendance = (sessionId) => {
  return useQuery({
    queryKey: ['attendance', sessionId],
    queryFn: () => sessionsApi.getAttendance(sessionId),
    enabled: !!sessionId,
  });
};

export const useMarkAttendance = (sessionId, courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attendances) => sessionsApi.markAttendance(sessionId, attendances),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessions', courseId] });
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
    },
  });
};

// ==================== MATERIALS HOOKS ====================

export const useMaterials = (courseId, sessionId = null) => {
  return useQuery({
    queryKey: ['materials', courseId, sessionId],
    queryFn: () => materialsApi.getByCourse(courseId, sessionId),
    enabled: !!courseId,
  });
};

export const useCreateMaterial = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: materialsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials', courseId] });
    },
  });
};

export const useDeleteMaterial = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: materialsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials', courseId] });
    },
  });
};

// ==================== GRADES HOOKS ====================

export const useGrades = (courseId, studentId = null) => {
  return useQuery({
    queryKey: ['grades', courseId, studentId],
    queryFn: () => gradesApi.getByCourse(courseId, studentId),
    enabled: !!courseId,
  });
};

export const useCreateGrade = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: gradesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grades', courseId] });
      queryClient.invalidateQueries({ queryKey: ['enrolled-students', courseId] });
    },
  });
};

export const useDeleteGrade = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: gradesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grades', courseId] });
    },
  });
};

// ==================== CERTIFICATES HOOKS ====================

export const useCertificates = (studentId = null, courseId = null) => {
  return useQuery({
    queryKey: ['certificates', studentId, courseId],
    queryFn: () => certificatesApi.getAll(studentId, courseId),
    enabled: true,
  });
};

export const useIssueCertificate = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: certificatesApi.issue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
    },
  });
};

// ==================== TEACHER HOOKS ====================

export const useTeacherStats = () => {
  return useQuery({
    queryKey: ['teacher-stats'],
    queryFn: teacherApi.getStats,
  });
};

export const useTeacherCourses = () => {
  return useQuery({
    queryKey: ['teacher-courses'],
    queryFn: teacherApi.getCourses,
  });
};

// ==================== STUDENT HOOKS ====================

export const useStudentCourses = () => {
  return useQuery({
    queryKey: ['student-courses'],
    queryFn: studentApi.getMyCourses,
  });
};

export const useStudentProgression = () => {
  return useQuery({
    queryKey: ['student-progression'],
    queryFn: studentApi.getProgression,
  });
};

// ==================== USERS HOOKS ====================

export const useTeachers = () => {
  return useQuery({
    queryKey: ['teachers'],
    queryFn: usersApi.getTeachers,
  });
};

// ==================== DOSSIERS HOOKS ====================

export const useDossiers = (filters = {}) => {
  return useQuery({
    queryKey: ['dossiers', filters],
    queryFn: () => dossiersApi.getAll(filters),
  });
};

export const useDossierDetail = (studentId) => {
  return useQuery({
    queryKey: ['dossier', studentId],
    queryFn: () => dossiersApi.getDetail(studentId),
    enabled: !!studentId,
  });
};

export const useGenerateDossierPdf = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dossiersApi.generatePdf(studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossier', studentId] });
      queryClient.invalidateQueries({ queryKey: ['dossiers'] });
    },
  });
};

export const useDossierAuditLogs = (studentId) => {
  return useQuery({
    queryKey: ['dossier-audit-logs', studentId],
    queryFn: () => dossiersApi.getAuditLogs(studentId),
    enabled: !!studentId,
  });
};

// ==================== DOCUMENTS HOOKS ====================

export const useDocuments = (studentId) => {
  return useQuery({
    queryKey: ['documents', studentId],
    queryFn: () => documentsApi.getByStudent(studentId),
    enabled: !!studentId,
  });
};

export const useUploadDocument = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentType, file }) => documentsApi.upload(studentId, documentType, file),
    onSuccess: () => {
      // Invalide TOUS les dossiers (correspond à 'self' ET à l'ID réel)
      queryClient.invalidateQueries({ queryKey: ['dossier'] });
      queryClient.invalidateQueries({ queryKey: ['dossiers'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

export const useReviewDocument = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, status, comment }) => documentsApi.review(documentId, status, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dossier'] });
      queryClient.invalidateQueries({ queryKey: ['dossiers'] });
    },
  });
};

export const useDeleteDocument = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: documentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', studentId] });
      queryClient.invalidateQueries({ queryKey: ['dossier', studentId] });
      queryClient.invalidateQueries({ queryKey: ['dossiers'] });
    },
  });
};

// ==================== APPOINTMENTS HOOKS ====================

export const useAppointments = (studentId) => {
  return useQuery({
    queryKey: ['appointments', studentId],
    queryFn: () => appointmentsApi.getByStudent(studentId),
    enabled: !!studentId,
  });
};

export const useCreateAppointment = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: appointmentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', studentId] });
      queryClient.invalidateQueries({ queryKey: ['dossier-audit-logs', studentId] });
    },
  });
};

export const useUpdateAppointment = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, data }) => appointmentsApi.update(appointmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', studentId] });
    },
  });
};

export const useDeleteAppointment = (studentId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: appointmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', studentId] });
    },
  });
};

// ==================== PAYMENTS HOOKS ====================

export const usePaymentStats = () => {
  return useQuery({
    queryKey: ['payment-stats'],
    queryFn: paymentsApi.getStats,
  });
};

export const usePaymentPlans = (filters = {}) => {
  return useQuery({
    queryKey: ['payment-plans', filters],
    queryFn: () => paymentsApi.getPlans(filters),
  });
};

export const usePaymentPlan = (planId) => {
  return useQuery({
    queryKey: ['payment-plan', planId],
    queryFn: () => paymentsApi.getPlan(planId),
    enabled: !!planId,
  });
};

export const useCreatePaymentPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
      queryClient.invalidateQueries({ queryKey: ['payment-stats'] });
    },
  });
};

export const useDeletePaymentPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.deletePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
      queryClient.invalidateQueries({ queryKey: ['payment-stats'] });
    },
  });
};

export const useInstallments = (planId) => {
  return useQuery({
    queryKey: ['installments', planId],
    queryFn: () => paymentsApi.getInstallments(planId),
    enabled: !!planId,
  });
};

export const useRecordPayment = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ installmentId, data }) => paymentsApi.recordPayment(installmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installments', planId] });
      queryClient.invalidateQueries({ queryKey: ['payment-plan', planId] });
      queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
      queryClient.invalidateQueries({ queryKey: ['payment-stats'] });
    },
  });
};

export const useCancelPayment = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.cancelPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installments', planId] });
      queryClient.invalidateQueries({ queryKey: ['payment-plan', planId] });
      queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
      queryClient.invalidateQueries({ queryKey: ['payment-stats'] });
    },
  });
};

export const useStudentPaymentSummary = (studentId) => {
  return useQuery({
    queryKey: ['student-payment-summary', studentId],
    queryFn: () => paymentsApi.getStudentSummary(studentId),
    enabled: !!studentId,
  });
};

// ==================== MESSAGING HOOKS ====================

export const useUnreadCount = () => {
  return useQuery({
    queryKey: ['unread-count'],
    queryFn: messagingApi.getUnreadCount,
    refetchInterval: 30000, // Poll every 30 seconds
  });
};

export const useConversations = (search = '') => {
  return useQuery({
    queryKey: ['conversations', search],
    queryFn: () => messagingApi.getConversations(search),
  });
};

export const useConversation = (conversationId) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => messagingApi.getConversation(conversationId),
    enabled: !!conversationId,
    refetchInterval: 10000, // Poll every 10 seconds for new messages
    onSuccess: () => {
      // Invalidate unread count when conversation is viewed
      queryClient.invalidateQueries({ queryKey: ['unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: messagingApi.createConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useSendMessage = (conversationId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ content, file }) => messagingApi.sendMessage(conversationId, content, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useDeleteMessage = (conversationId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId) => messagingApi.deleteMessage(conversationId, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
  });
};

export const useMessageableUsers = (search = '') => {
  return useQuery({
    queryKey: ['messageable-users', search],
    queryFn: () => messagingApi.getMessageableUsers(search),
    enabled: true,
  });
};

// ==================== NOTIFICATIONS HOOKS ====================

export const useNotificationsUnreadCount = () => {
  return useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: notificationsApi.getUnreadCount,
    refetchInterval: 30000, // Poll every 30 seconds
  });
};

export const useNotifications = (params = {}) => {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => notificationsApi.getAll(params),
    refetchInterval: 30000,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

export const useClearAllNotifications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.clearAll,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

// ==================== STATISTICS HOOKS ====================

export const useGlobalStats = () => {
  return useQuery({
    queryKey: ['global-stats'],
    queryFn: statsApi.getGlobal,
    refetchInterval: 60000, // Poll every minute
  });
};

export const useStudentStats = () => {
  return useQuery({
    queryKey: ['student-stats'],
    queryFn: statsApi.getStudents,
    refetchInterval: 60000,
  });
};

export const usePaymentStatsDetailed = () => {
  return useQuery({
    queryKey: ['payment-stats-detailed'],
    queryFn: statsApi.getPayments,
    refetchInterval: 60000,
  });
};

export const useDossierStats = () => {
  return useQuery({
    queryKey: ['dossier-stats'],
    queryFn: statsApi.getDossiers,
    refetchInterval: 60000,
  });
};

export const useExportReport = () => {
  return useMutation({
    mutationFn: ({ type, format, filters }) => statsApi.exportReport(type, format, filters),
  });
};

// ==================== AVATAR HOOKS ====================

export const useUploadAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: avatarApi.upload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
};

export const useDeleteAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: avatarApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
};

// ==================== CRON HOOKS ====================

export const useRunPaymentReminders = () => {
  return useMutation({
    mutationFn: cronApi.runPaymentReminders,
  });
};

// ==================== RESOURCES HOOKS ====================

export const useResources = (params = {}) => {
  return useQuery({
    queryKey: ['resources', params],
    queryFn: () => resourcesApi.getAll(params),
  });
};

export const useFAQ = () => {
  return useQuery({
    queryKey: ['faq'],
    queryFn: resourcesApi.getFAQ,
  });
};

export const useCreateResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resourcesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
    },
  });
};

export const useUpdateResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ resourceId, data }) => resourcesApi.update(resourceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
    },
  });
};

export const useDeleteResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resourcesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
    },
  });
};
